import { memo, useMemo, type FC } from 'react';

import {
  Chip,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import type { ApproverEntity } from '../types/shared.js';

type Props = {
  items: ApproverEntity[];
  maxVisibleChipsCount: number;
  tooltipContentSeparator?: string;
};

const ChipGroup: FC<Props> = memo(
  ({ items, maxVisibleChipsCount, tooltipContentSeparator = ' ' }) => {
    const { visibleItems, remainingCount, tooltipText } = useMemo(() => {
      const visible = items.slice(0, maxVisibleChipsCount);
      const hidden = items.slice(maxVisibleChipsCount);
      const hiddenEntities = hidden.filter((item) => item.type !== 'operation');

      return {
        visibleItems: visible,
        remainingCount: hiddenEntities.length,
        tooltipText: hidden
          .map((item) => item.label)
          .join(tooltipContentSeparator),
      };
    }, [items, maxVisibleChipsCount, tooltipContentSeparator]);

    const showRemainingCountChip = remainingCount > 0;

    return (
      <div className="flex h-full flex-wrap items-center gap-x-2 gap-y-1">
        {visibleItems.map((item, index) => {
          const key = item.id + index;
          return item.type === 'operation' ? (
            <span
              key={key}
              className={cn(
                typographyVariants({
                  variant: 'bodyShort01',
                  color: 'secondary',
                }),
                'uppercase',
              )}
            >
              {item.label}
            </span>
          ) : (
            <Chip key={key} variant="outline" color="silver" size="medium">
              {item.label}
            </Chip>
          );
        })}

        {showRemainingCountChip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Chip variant="filled" color="silver" size="medium">
                +{remainingCount}
              </Chip>
            </TooltipTrigger>
            <TooltipContent>{tooltipText}</TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    );
  },
);

export default ChipGroup;
